import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { setSiteLayout } from "@/lib/siteLayout";
import { SOCIAL_ICONS } from "@/components/Footer";

const inp = "w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-primary";
const btn = "p-2 rounded-lg border border-border hover:border-primary transition-colors disabled:opacity-30";
const PLATFORMS = ["instagram", "facebook", "youtube", "x", "tiktok", "linkedin", "pinterest", "threads", "telegram", "discord", "website"];

const move = (arr, i, d) => { const a = [...arr]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; };

function Section({ title, hint, children, action }) {
  return (
    <section className="rounded-2xl border border-border surface-2 p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div><h3 className="text-lg font-display font-bold">{title}</h3>{hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}</div>
        {action}
      </div>
      {children}
    </section>
  );
}

function LinkList({ links, onChange, testid }) {
  const upd = (i, patch) => onChange(links.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  return (
    <div className="space-y-2">
      {links.map((l, i) => (
        <div key={i} className="flex flex-wrap md:flex-nowrap items-center gap-2" data-testid={`${testid}-row-${i}`}>
          <input className={inp} placeholder="Label" value={l.label} onChange={(e) => upd(i, { label: e.target.value })} data-testid={`${testid}-label-${i}`} />
          <input className={inp} placeholder="/path or https://…" value={l.url} onChange={(e) => upd(i, { url: e.target.value })} data-testid={`${testid}-url-${i}`} />
          <label className="flex items-center gap-1 text-xs whitespace-nowrap"><input type="checkbox" checked={!!l.new_tab} onChange={(e) => upd(i, { new_tab: e.target.checked })} /> New tab</label>
          <button className={btn} disabled={i === 0} onClick={() => onChange(move(links, i, -1))} data-testid={`${testid}-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
          <button className={btn} disabled={i === links.length - 1} onClick={() => onChange(move(links, i, 1))} data-testid={`${testid}-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
          <button className={`${btn} text-destructive`} onClick={() => onChange(links.filter((_, k) => k !== i))} data-testid={`${testid}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button onClick={() => onChange([...links, { label: "", url: "/", new_tab: false }])} data-testid={`${testid}-add`} className="inline-flex items-center gap-1 text-sm text-primary mt-1"><Plus className="h-4 w-4" /> Add link</button>
    </div>
  );
}

function FooterColumns({ cols, onChange }) {
  const upd = (i, patch) => onChange(cols.map((c, k) => (k === i ? { ...c, ...patch } : c)));
  return (
    <div className="space-y-4">
      {cols.map((c, i) => (
        <div key={i} className="rounded-xl border border-border p-4 space-y-3" data-testid={`footer-col-${i}`}>
          <div className="flex items-center gap-2">
            <input className={`${inp} font-semibold`} placeholder="Column title" value={c.title} onChange={(e) => upd(i, { title: e.target.value })} data-testid={`footer-col-title-${i}`} />
            <button className={btn} disabled={i === 0} onClick={() => onChange(move(cols, i, -1))}><ArrowUp className="h-4 w-4" /></button>
            <button className={btn} disabled={i === cols.length - 1} onClick={() => onChange(move(cols, i, 1))}><ArrowDown className="h-4 w-4" /></button>
            <button className={`${btn} text-destructive`} onClick={() => onChange(cols.filter((_, k) => k !== i))} data-testid={`footer-col-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
          <LinkList links={c.links} onChange={(links) => upd(i, { links })} testid={`footer-col-${i}-link`} />
        </div>
      ))}
    </div>
  );
}

function Socials({ items, onChange }) {
  const upd = (i, patch) => onChange(items.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  return (
    <div className="space-y-2">
      {items.map((s, i) => {
        const Icon = SOCIAL_ICONS[s.platform];
        return (
          <div key={i} className="flex items-center gap-2" data-testid={`social-row-${i}`}>
            {Icon && <Icon className="h-5 w-5 text-primary shrink-0" />}
            <select className={`${inp} w-40`} value={s.platform} onChange={(e) => upd(i, { platform: e.target.value })} data-testid={`social-platform-${i}`}>
              {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <input className={inp} placeholder="https://… (leave empty to hide)" value={s.url} onChange={(e) => upd(i, { url: e.target.value })} data-testid={`social-url-${i}`} />
            <button className={`${btn} text-destructive`} onClick={() => onChange(items.filter((_, k) => k !== i))} data-testid={`social-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        );
      })}
      <button onClick={() => onChange([...items, { platform: "instagram", url: "" }])} data-testid="social-add" className="inline-flex items-center gap-1 text-sm text-primary mt-1"><Plus className="h-4 w-4" /> Add social link</button>
    </div>
  );
}

export default function SiteLayoutCMS() {
  const [L, setL] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { api.get("/site-layout").then((r) => setL(r.data)); }, []);
  if (!L) return null;
  const set = (patch) => { setL({ ...L, ...patch }); setDirty(true); };
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/site-layout", L); setSiteLayout(data); setDirty(false); toast.success("Header & footer published"); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const SaveBtn = (
    <button onClick={save} disabled={busy || !dirty} data-testid="layout-save-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-50">
      <Save className="h-4 w-4" /> {busy ? "Saving…" : dirty ? "Save & Publish" : "Saved"}
    </button>
  );
  return (
    <div className="space-y-6" data-testid="site-layout-cms">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">Edit the public header & footer. Changes go live for all readers when you publish.</p>
        {SaveBtn}
      </div>
      <Section title="Header navigation" hint="Shown in the top bar (desktop) and mobile menu. Use /path for internal pages or https:// for external.">
        <LinkList links={L.header_links} onChange={(header_links) => set({ header_links })} testid="header-link" />
      </Section>
      <Section title="Contact details" hint="Displayed in the footer under the logo.">
        <div className="grid md:grid-cols-3 gap-3">
          <input className={inp} placeholder="Email" value={L.contact?.email || ""} onChange={(e) => set({ contact: { ...L.contact, email: e.target.value } })} data-testid="contact-email" />
          <input className={inp} placeholder="Phone" value={L.contact?.phone || ""} onChange={(e) => set({ contact: { ...L.contact, phone: e.target.value } })} data-testid="contact-phone" />
          <input className={inp} placeholder="Address" value={L.contact?.address || ""} onChange={(e) => set({ contact: { ...L.contact, address: e.target.value } })} data-testid="contact-address" />
        </div>
      </Section>
      <Section title="Footer text">
        <div className="grid md:grid-cols-2 gap-3">
          <input className={inp} placeholder="Tagline" value={L.tagline} onChange={(e) => set({ tagline: e.target.value })} data-testid="footer-tagline-input" />
          <input className={inp} placeholder="Copyright" value={L.copyright} onChange={(e) => set({ copyright: e.target.value })} data-testid="footer-copyright-input" />
        </div>
      </Section>
      <Section title="Footer columns" hint="Each column has a title and its own links."
        action={<button onClick={() => set({ footer_columns: [...L.footer_columns, { title: "New column", links: [] }] })} data-testid="footer-col-add" className="inline-flex items-center gap-1 text-sm text-primary"><Plus className="h-4 w-4" /> Add column</button>}>
        <FooterColumns cols={L.footer_columns} onChange={(footer_columns) => set({ footer_columns })} />
      </Section>
      <Section title="Social links" hint="Icons appear in the footer; empty URLs are hidden.">
        <Socials items={L.socials} onChange={(socials) => set({ socials })} />
      </Section>
      <div className="flex justify-end">{SaveBtn}</div>
    </div>
  );
}
